import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import MolstarViewer, {MolstarWrapperProps} from 'molbio/molstar/Viewer';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';
import {CenteredSpinner} from 'ui-library/Spinner';

import ExternalCSVFile from './ExternalCSVFile';
import ExternalFileChip from './ExternalFileChip';
import ExternalFileIFrame from './ExternalFileIFrame';
import ExternalFileImage from './ExternalFileImage';
import ExternalFileRenderer from './ExternalFileRenderer';
import {GetScratchFile, GetScratchFileVariables} from './network/__generated/GetScratchFile';
import {GET_SCRATCH_FILE_QUERY} from './network/GetScratchFile';

interface ExternalFilePaneContentProps {
  scratchFile: GetAnalysisScratchFile;
}

/** @package */
export const MOLSTAR_MIME_TYPE_TO_FORMAT: Record<string, MolstarWrapperProps['format']> = {
  'chemical/x-pdb': 'pdb',
  'chemical/x-pdbqt': 'pdbqt',
  'chemical/x-mmcif': 'mmcif',
};

export function renderFilePreview(scratchFile: GetAnalysisScratchFile): JSX.Element | null {
  const {mimeType} = scratchFile;
  switch (mimeType) {
    // In the future, we could support `HTML` as a first-class `EXTERNAL_FILE_TYPE`, so that embedded HTML
    // previews are supported across the whole platform (i.e., for all `ExternalFile` callers). For now, we
    // scope it down to Analyses.
    case 'text/html':
      return (
        <ExternalFileIFrame
          scratchFile={scratchFile}
          externalFileWarning="This block contains a file that is external to Benchling."
          // We allow scripts in the iframe because they are needed to render HTML reports generated by JMP.
          dangerouslyAllowScriptsInIFrame={true}
          // File ExternalFiles do not have their Content-Type metadata set, so they default to
          // application/octet-stream. Therefore, we need to tell the S3 signing endpoint to override the
          // Content-Type header to text/html.
          dangerouslyOverrideHTMLFileContentTypeHeader={true}
        />
      );
    case 'application/json':
    case 'text/plain':
      return (
        <ExternalFileRenderer scratchFile={scratchFile} fileType="text" loadContent={true}>
          {(content) => (
            <textarea
              className={css(styles.textPreview)}
              value={content}
              readOnly={true}
              placeholder="Empty file"
            />
          )}
        </ExternalFileRenderer>
      );
    case 'text/markdown':
      return (
        <ExternalFileRenderer scratchFile={scratchFile} fileType="markdown" loadContent={true}>
          {(content) => (
            <div className={css(styles.markdownPreview)}>
              <MarkdownContent content={content} />
            </div>
          )}
        </ExternalFileRenderer>
      );
    case 'text/csv':
    case 'text/tsv':
      return <ExternalCSVFile scratchFile={scratchFile} />;
    case 'image/jpeg':
    case 'image/png':
      return <ExternalFileImage scratchFile={scratchFile} />;
    case 'chemical/x-pdb':
    case 'chemical/x-pdbqt':
    case 'chemical/x-mmcif': {
      const format = MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType];
      return (
        <ExternalFileRenderer scratchFile={scratchFile} fileType={format} loadContent={false}>
          {(signedURL) => (
            <MolstarViewer url={signedURL} format={format} className={css(styles.molstarViewer)} />
          )}
        </ExternalFileRenderer>
      );
    }
    default:
      return null;
  }
}

/**
 * Render `Pane` content for the given `ExternalFile` for Analysis use cases
 *
 * As of writing, those use cases are:
 *   - Embedded HTML for HTML reports
 *   - Text files for plain text content (.txt, .rtf files)
 *   - Markdown files for markdown content (.md files)
 *   - CSV files for CSV content (.csv, .tsv files)
 *   - File chip for JMP and JRP files
 *
 * This component is intentionally written to be `File`-agnostic, so that it may share code with other
 * `ExternalFile` callers more easily in the future.
 */
export default function ExternalFilePaneContent({scratchFile}: ExternalFilePaneContentProps): JSX.Element {
  const filePreview = renderFilePreview(scratchFile);
  return filePreview ?? <ExternalFileChip scratchFile={scratchFile} />;
}

interface QueryingExternalFilePaneContentProps {
  fileId: string;
}

export function QueryingExternalFilePaneContent({fileId}: QueryingExternalFilePaneContentProps): JSX.Element {
  return (
    <BenchlingQuery<GetScratchFile, GetScratchFileVariables>
      query={GET_SCRATCH_FILE_QUERY}
      variables={{id: fileId}}
      renderLoading={() => <CenteredSpinner />}
    >
      {(data) => {
        if (isInaccessibleInfo(data.scratchFile)) {
          return (
            <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this file." />
          );
        }

        return <ExternalFilePaneContent scratchFile={data.scratchFile} />;
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  textPreview: {
    ...FONT_PROXIMA_13,
    width: '100%',
    height: '100%',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    resize: 'none',
    padding: 12,
    fontFamily: FONT_FAMILY_MONOSPACED,
    backgroundColor: GRAY_LIGHTER,
    color: NEAR_BLACK,
  },
  markdownPreview: {
    ...FONT_PROXIMA_13,
  },
  molstarViewer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    // We need a minimum height, because if we ever try to render a molstar viewer with a height of 0, it will throw an error.
    minWidth: 10,
    minHeight: 10,
  },
});
